import { useContext, useMemo, useState } from "react";
import PropTypes from "prop-types";
import UserContext from "common/Context/UserContext";
import { resolveParentCandidatesForNodeType } from "pages/DataManagement/Tags/tagRowsMapper";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import ClearIcon from "@mui/icons-material/Clear";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Tooltip from "@mui/material/Tooltip";
import SelectParentDialog from "common/Components/BulkEditTagsDialog/SelectParentDialog";
import { buildAllowedParentTree } from "common/Components/BulkEditTagsDialog/branchTreeData";
import { formatParentNameWithId } from "pages/DataManagement/Tags/tagRowsMapper";
import {
  isFiniteNumber,
  isNullUndefinedOrEmptyString,
} from "common/utils/valueChecks";
import { BulkFieldTextField } from "common/Components/BulkEditTagsDialog/BulkEditTagsDialog.styles";

const selectInputLabelShrink = { shrink: true };

export default function ParentNodeTreePicker({
  label,
  nodeType,
  referencePayload,
  parentCandidates,
  disabled,
  valueId,
  displayNameOverride,
  onChange,
  inputTestId,
  placeholder,
}) {
  const { getCustomerName } = useContext(UserContext);
  const [dialogOpen, setDialogOpen] = useState(false);
  const customerName = getCustomerName?.() ?? "";

  const resolvedParentCandidates = useMemo(
    () =>
      resolveParentCandidatesForNodeType(
        referencePayload,
        nodeType,
        parentCandidates,
        customerName
      ),
    [referencePayload, nodeType, parentCandidates, customerName]
  );

  const treeRoot = useMemo(
    () =>
      buildAllowedParentTree(
        referencePayload,
        nodeType,
        parentCandidates,
        customerName
      ),
    [referencePayload, nodeType, parentCandidates, customerName]
  );

  const displayText = useMemo(() => {
    if (isNullUndefinedOrEmptyString(valueId)) return "";
    if (!isFiniteNumber(valueId)) return "";
    const idNum = Number(valueId);
    const match = resolvedParentCandidates.find(
      (c) => Number(c.nodeId) === idNum
    );
    const nameFromCandidate =
      match?.nodeName != null && String(match.nodeName).trim() !== ""
        ? String(match.nodeName).trim()
        : "";
    const override =
      displayNameOverride != null && String(displayNameOverride).trim() !== ""
        ? String(displayNameOverride).trim()
        : "";
    const namePart = nameFromCandidate || override;
    return formatParentNameWithId(namePart, idNum);
  }, [valueId, resolvedParentCandidates, displayNameOverride]);

  const canBrowse = Boolean(nodeType) && !disabled && treeRoot;

  const handleConfirm = ({ parentNodeId }) => {
    const id = Number(parentNodeId);
    if (!Number.isFinite(id)) {
      onChange?.({ parentNodeId: null, parentNode: "" });
      return;
    }
    onChange?.({ parentNodeId: id, parentNode: String(id) });
  };

  const handleClear = (event) => {
    event.stopPropagation();
    onChange?.({ parentNodeId: null, parentNode: "" });
  };

  return (
    <>
      <BulkFieldTextField
        label={label}
        size="small"
        fullWidth
        disabled={disabled}
        value={displayText}
        placeholder={placeholder}
        InputLabelProps={label ? selectInputLabelShrink : undefined}
        InputProps={{
          readOnly: true,
          endAdornment: (
            <InputAdornment position="end">
              {displayText && !disabled ? (
                <Tooltip title="Clear parent">
                  <IconButton
                    size="small"
                    aria-label="Clear parent node"
                    onClick={handleClear}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              ) : null}
              <Tooltip
                title={
                  canBrowse
                    ? "Open searchable hierarchy to pick a parent"
                    : !nodeType
                      ? "Select a node type first"
                      : !treeRoot
                        ? "No valid parent branches for this type"
                        : ""
                }
              >
                <span>
                  <IconButton
                    size="small"
                    disabled={!canBrowse}
                    aria-label="Select parent from tree"
                    onClick={() => setDialogOpen(true)}
                    data-testid={inputTestId}
                  >
                    <AccountTreeOutlinedIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </InputAdornment>
          ),
          inputProps: inputTestId
            ? { "data-testid": `${inputTestId}-display` }
            : undefined,
        }}
      />
      <SelectParentDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onConfirm={handleConfirm}
        initialValue={displayText}
        treeRoot={treeRoot}
        loading={false}
      />
    </>
  );
}

ParentNodeTreePicker.propTypes = {
  label: PropTypes.string,
  nodeType: PropTypes.string,
  referencePayload: PropTypes.object,
  parentCandidates: PropTypes.arrayOf(PropTypes.object),
  disabled: PropTypes.bool,
  valueId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  displayNameOverride: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  inputTestId: PropTypes.string,
  placeholder: PropTypes.string,
};

ParentNodeTreePicker.defaultProps = {
  label: undefined,
  nodeType: "",
  referencePayload: null,
  parentCandidates: [],
  disabled: false,
  valueId: null,
  displayNameOverride: undefined,
  inputTestId: undefined,
  placeholder: "",
};
